import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  WhatsNewCreatePayload,
  WhatsNewReorderPayload,
  WhatsNewStatusTogglePayload,
  WhatsNewUpdatePayload,
} from 'Modules'

import { AppAdmin } from 'api/app-admin'
import { logError } from 'utils/logger'

import { whatsNewKeys } from './query-keys'

const invalidateLists = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: whatsNewKeys.lists() })

export const useCreateWhatsNew = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: WhatsNewCreatePayload) => {
      const res = await AppAdmin.createWhatsNew(payload)
      return res.data?.data
    },
    onSuccess: () => {
      invalidateLists(queryClient)
    },
    onError: (err) => logError('Create whats new failed', err),
  })
}

export const useUpdateWhatsNew = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: WhatsNewUpdatePayload }) => {
      const res = await AppAdmin.updateWhatsNew(id, payload)
      return res.data?.data
    },
    onSuccess: (_data, vars) => {
      invalidateLists(queryClient)
      queryClient.invalidateQueries({ queryKey: whatsNewKeys.detail(vars.id) })
    },
    onError: (err) => logError('Update whats new failed', err),
  })
}

export const useUpdateWhatsNewStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: WhatsNewStatusTogglePayload }) => {
      const res = await AppAdmin.updateWhatsNewStatus(id, payload)
      return res.data?.data
    },
    onSuccess: () => {
      invalidateLists(queryClient)
    },
    onError: (err) => logError('Update whats new status failed', err),
  })
}

export const useDeleteWhatsNew = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      await AppAdmin.deleteWhatsNew(id)
      return id
    },
    onSuccess: () => {
      invalidateLists(queryClient)
    },
    onError: (err) => logError('Delete whats new failed', err),
  })
}

export const useReorderWhatsNew = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: WhatsNewReorderPayload) => {
      const res = await AppAdmin.reorderWhatsNew(payload)
      return res.data?.data
    },
    onSuccess: () => {
      invalidateLists(queryClient)
    },
    onError: (err) => logError('Reorder whats new failed', err),
  })
}
